/**
 * @fileoverview News Searcher Module.
 * This module sets up and initializes the Algolia InstantSearch for the news section.
 * It includes configurations for search, custom widgets, and pagination.
 */

import { elementExists, getFullIndexName } from '../Utils';
import instantsearch from 'instantsearch.js';
import { configure, searchBox } from 'instantsearch.js/es/widgets';
import algoliasearch from 'algoliasearch';
import { connectHits } from "instantsearch.js/es/connectors";
import { clearRefinementsComponent } from "./widgets/NewsClearFilters";
import { clearRefinementsMobileComponent } from "./widgets/NewsClearRefinementsMobile";
import customPagination from './widgets/CustomPagination';
import { refinementComponent } from "./widgets/NewsRefinementList";
import { refinementMobileComponent } from "./widgets/NewsRefinementMobileList";
import NewsHits from "./widgets/NewsHits";
import { gtmSendSearchData, updateResultCounter, urlUpdateHandler } from "./Utils";
import { debounceEvent } from "../Utils/debounceEvent";

/**
 * @constant
 * @type {object}
 * @description Algolia search client instance configured with application ID and search-only API key.
 */
const searchClient = algoliasearch(import.meta.env.VITE_ALGOLIA_APP_ID, import.meta.env.VITE_ALGOLIA_READ_KEY);

const newsContainerSelector = '#news-container';
const newsTypeRefinementSelector = '#news-type-refinement-container';
const searchboxSelector = '#searchbox';
const hitsSelector = '#news_hits';
const indexName = 'post';
const clearFiltersLabel = 'All our news';
const paginationSelector = '#pagination';
let dataLayerTimeout = null;
const fullIndexName = getFullIndexName(indexName);

/**
 * Initializes the news searcher module.
 * Checks if the news container exists and initializes the searcher if it does.
 */
const init = () => {
    if (!elementExists(newsContainerSelector)) return;
    setListeners();
    initSearcher();
};

const setListeners = () => {
    document.addEventListener('urlUpdateEvent', urlUpdateHandler);
    document.getElementById('searchbox').addEventListener('input', inputHandler);
}

const inputHandler = (e) => {
    if (e.target.value != '') {
        document.getElementById('trending-news-container').classList.add('hidden');
    } else {
        document.getElementById('trending-news-container').classList.remove('hidden');
    }
}

/**
 * Updates the search title based on the provided value.
 * @param {string|null} value - The value to be set as the title. If null, sets to default title.
 */
const updateSearchTitle = (value) => {
    if (!value) {
        value = `All our <span class="text-body-xx-large italic font-medium font-serif mt-1">news</span>`;
    }
    document.getElementById('news-search-title').innerHTML = value;
}

const urlParams = new URLSearchParams(window.location.search);
const newsTypeRefinementValue = urlParams.get(`nt`)?.split(',')[0];


if (urlParams.size > 0) {
    const newsFilter = document.getElementById('news-filter');
    if (newsFilter) {
        newsFilter.scrollIntoView({
            behavior: 'smooth'
        });
    }
}

let previousRefinementValue = newsTypeRefinementValue;
if (previousRefinementValue) {
    updateSearchTitle(previousRefinementValue);
}

/**
 * Initializes and configures the Algolia InstantSearch for the news section.
 * Sets up search parameters, custom widgets, and starts the search process.
 */
const initSearcher = () => {
    const search = instantsearch({
        indexName: fullIndexName,
        searchClient,
        routing: {
            stateMapping: {
                stateToRoute(uiState) {
                    const indexUiState = uiState[getFullIndexName(indexName)] || {};
                    let routeState = {};
                    if (indexUiState.query) {
                        routeState.q = indexUiState.query;
                    }
                    if (indexUiState.refinementList?.news_type) {
                        routeState.nt = indexUiState.refinementList.news_type.join(',');
                    }
                    routeState.pg = indexUiState.page;

                    debounceEvent('urlUpdateEvent', routeState);
                    return routeState;
                },
                routeToState(routeState) {
                    let state = {
                        [getFullIndexName(indexName)]: {
                            query: routeState.q || '',
                            refinementList: {},
                            page: routeState.pg || 1
                        }
                    };

                    if (routeState.nt) {
                        state[getFullIndexName(indexName)].refinementList.news_type = routeState.nt.split(',').map(decodeURIComponent);
                    }
                    state[getFullIndexName(indexName)].page = routeState.pg || 1;
                    return state;
                }
            }
        },
    });

    /**
     * Updates the label of filters based on the current hits and key.
     * @param {Array} hits - The current array of hits from Algolia.
     * @param {string} key - The key to check in the filters.
     */
    const updateFiltersLabel = (hits, key) => {
        const dropdownLabelElement = document.querySelector('.dropdown-label-js');
        if (!dropdownLabelElement) return;

        const appliedFilters = hits.instantSearchInstance.helper?.state.disjunctiveFacetsRefinements;
        const items = appliedFilters ? appliedFilters[key] : false;

        if (!items || (items && !items.length)) {
            dropdownLabelElement.textContent = clearFiltersLabel;
            return
        }

        dropdownLabelElement.textContent = items[0];
    };

    /**
     * Custom hits connector for Algolia InstantSearch.
     * This connector customizes the display of news hits and updates filters and result counters.
     * @type {function}
     */
    const customHits = connectHits((hits) => {
        NewsHits(hits);
        updateResultCounter(hits);
        updateFiltersLabel(hits, 'news_type');
        dataLayerTimeout = gtmSendSearchData(dataLayerTimeout, hits, 'news-search-ev');
    });

    search.addWidgets([
        configure({
            hitsPerPage: 12,
        }),
        searchBox({
            container: searchboxSelector,
            placeholder: 'Search our news',
            showSubmit: true,
            templates: {
                submit: '<div class="absolute left-3 top-6"><svg class="h-[18.28px]" width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
                    '<path d="M12.877 13.7927L17.612 18.5798" stroke="black"/>\n' +
                    '<path d="M13.73 7.13414C13.73 10.6401 10.9202 13.4729 7.46557 13.4729C4.01097 13.4729 1.20117 10.6401 1.20117 7.13414C1.20117 3.62819 4.01097 0.79541 7.46557 0.79541C10.9202 0.79541 13.73 3.62819 13.73 7.13414Z" stroke="#2A2824"/>\n' +
                    '</svg></div>'
            },
        }),
        refinementComponent({
            componentSelector: newsTypeRefinementSelector,
            attribute: 'news_type',
            sortBy: ['name:asc'],
            updatePreviousRefinementValue: (value) => {
                updateSearchTitle(value);
                previousRefinementValue = value;
            },
            getPreviousRefinementValue: () => previousRefinementValue,
        }),
        refinementMobileComponent({
            componentSelector: '#content-type-refinement-container',
            attribute: 'news_type',
            sortBy: ['name:asc'],
            updatePreviousRefinementValue: (value) => {
                updateSearchTitle(value);
                previousRefinementValue = value;
            },
            getPreviousRefinementValue: () => previousRefinementValue,
        }),
        clearRefinementsComponent({
            container: '#clear-refinements',
            label: clearFiltersLabel,
            updatePreviousRefinementValue: (value) => {
                updateSearchTitle();
                previousRefinementValue = value;
            },
        }
        ),
        clearRefinementsMobileComponent({
            container: '#clearRefinementsButton',
            label: clearFiltersLabel,
            updatePreviousRefinementValue: (value) => {
                updateSearchTitle();
                previousRefinementValue = value;
            },
        }
        ),
        customHits({
            container: document.querySelector(hitsSelector),
        }),
        customPagination({
            container: document.querySelector(paginationSelector),
            topContainer: 'news-filter',
        })
    ])
        ;

    search.start();
};

init();
